import { FunctionComponent } from 'react';
import { withStyles } from '../../../utils/utils';
import styles from './Box.module.scss';

export type BoxProps = {
  /**
    `padding-top`
    ```ts
    sm:  10px;
    smd: 15px;
    lmd: 18px;
    md:  20px;
    xmd: 30px;
    lg:  40px;
    ```
  */
  pt?: 'sm' | 'smd' | 'md' | 'lmd' | 'xmd' | 'lg';

  /**
    `padding-bottom`
    ```ts
    sm:  10px;
    smd: 15px;
    lmd: 18px;
    md:  20px;
    xmd: 30px;
    lg:  40px;
    ```
  */
  pb?: 'sm' | 'smd' | 'md' | 'lmd' | 'xmd' | 'lg';

  /**
    `padding-left`
    ```ts
    sm:  10px;
    smd: 15px;
    lmd: 18px;
    md:  20px;
    xmd: 30px;
    lg:  40px;
    ```
  */
  ps?: 'sm' | 'smd' | 'md' | 'lmd' | 'xmd' | 'lg' | 'none';

  /**
    `padding-right`
    ```ts
    sm:  10px;
    smd: 15px;
    lmd: 18px;
    md:  20px;
    xmd: 30px;
    lg:  40px;
    ```
  */
  pe?: 'sm' | 'smd' | 'md' | 'lmd' | 'xmd' | 'lg';
  /**
    `padding`
    ```ts
    sm:  10px;
    smd: 15px;
    lmd: 18px;
    md:  20px;
    xmd: 30px;
    lg:  40px;
    ```
  */
  p?: 'sm' | 'smd' | 'md' | 'lmd' | 'xmd' | 'lg';
  /**
    `margin-top`
    ```ts
    sm:    10px;
    smmd:  15px;
    md:    20px;
    xmd:   30px;
    lg:    40px;
    xl:    50px;
    ```
  */
  mt?: 'sm' | 'smmd' | 'md' | 'xmd' | 'lg' | 'xl';

  /**
    `margin-bottom`
    ```ts
    sm:    10px;
    smmd:  15px;
    md:    20px;
    xmd:   30px;
    lg:    40px;
    xl:    50px;
    ```
  */
  mb?: 'sm' | 'smmd' | 'md' | 'xmd' | 'lg' | 'xl';

  /**
    `border-radius`
    ```ts
    sm:    3px;
    md:    10px;
    lg:    30px;
    ```
  */
  r?: 'sm' | 'md' | 'lg';

  /**
    `flex-direction`
    ```ts
    col:    column;
    row:    row;
    ```
  */
  flx?: 'col' | 'row';

  /**
    `flex-wrap`
    ```ts
    wrap:    wrap;
    reverse: wrap-reverse;
    ```
  */
  wrap?: 'wrap' | 'reverse';

  /**
    `overflow`
    ```ts
    auto:    auto;
    hidden:  hidden;
    ```
  */
  ov?: 'auto' | 'hidden';
  /**
    `gap`
    ```ts
    sm:      10px;
    md:      20px;
    lmd:     18px;
    ml:      30px;
    lg:      40px;
    xl:      120px;
    none:    0px;
    common:  24px;
    ```
  */
  gap?: 'sm' | 'md' | 'lmd' | 'ml' | 'lg' | 'xl' | 'none' | 'common';
  gapSize?: string;

  /**
    `flex-grow`
    ```ts
    1:   flex-grow: 1;
    2:   flex-grow: 2;
    3:   flex-grow: 3;
    4:   flex-grow: 4;
    ```
  */
  grow?: '1' | '2' | '3' | '4';
  base?: '5' | '8';

  /**
    `align-items`
    ```ts
    center:  center;
    fs:      flex-start;
    fe:      flex-end;
    stretch: stretch;
    baseline: baseline;
    ```
  */
  ai?: 'center' | 'fs' | 'fe' | 'stretch' | 'baseline';

  /**
    `align-content`
    ```ts
    center:  center;
    fe:      flex-end;
    fs:      flex-start;
    sa:      space-around;
    sb:      space-between;
    se:      space-evenly;
    stretch: stretch;
    ```
  */
  ac?: 'center' | 'fe' | 'fs' | 'sa' | 'sb' | 'se';

  /**
    `justify-content`
    ```ts
    center:  center;
    fs:      flex-start;
    fe:      flex-end;
    sa:      space-around;
    sb:      space-between;
    se:      space-evenly;
    st:      start;
    ```
  */
  jc?: 'center' | 'fe' | 'fs' | 'sa' | 'sb' | 'se' | 'st';

  /**
    ```ts
    .boundary-shadow {
      border-radius: 5px;
      box-shadow: 0px 4px 10px 0px $box-shadow;
    }
    .boundary-border {
      border-radius: 3px;
      border: 1px solid #dce0e5;
      box-shadow: 0 2px 4px 0 rgba(195, 195, 195, 0.56);
    }
    ```
  */
  boundary?: 'shadow' | 'border';

  /**
    ```ts
    .height-auto {
      height: auto;
    }

    .height-full {
      height: 100vh;
    }
    ```
  */
  height?: 'auto' | 'full';

  maxheight?: string;
  minheight?: string;

  /**
    ```ts
    .background-grey {
      background-color: #F6F6F6;
    }
    .background-white {
      background-color: $vopayWhite;
    }
    .background-yellow {
      background-color: #FFE391;
    }
    ```
  */
  background?: 'auto' | 'grey' | 'white' | 'yellow' | 'pure-white';

  /**
    ```ts
    .position-relative {
      position: relative;
    }

    .position-absolute {
      position: absolute;
    }
    ```
  */
  position?: 'relative' | 'absolute' | 'static';
  width?: string;
  boxWidth?: string;
  maxwidth?: string;
  lineHeight?: string;
  padding?: string;
  borderRadius?: string;
  boxShadow?: string;
  isInactive?: boolean;
  children?: React.ReactNode;
  className?: string;
};

type FC = FunctionComponent<BoxProps>;

const Box: FC = ({
  ai,
  ac,
  pt,
  pb,
  ps,
  pe,
  p,
  mt,
  mb,
  r,
  base = '5',
  flx,
  wrap,
  gap,
  gapSize,
  grow,
  jc,
  boundary,
  ov,
  background = 'auto',
  height,
  position = 'static',
  maxheight = 'auto',
  minheight = 'auto',
  width = 'auto',
  maxwidth = 'auto',
  boxWidth,
  lineHeight,
  padding,
  borderRadius,
  boxShadow,
  isInactive,
  children,
  className,
}: BoxProps) => {
  const classes = withStyles(styles);

  return (
    <div
      className={classes(
        'container',
        `pt-${pt || ''}`,
        `pb-${pb || ''}`,
        `ps-${ps || ''}`,
        `pe-${pe || ''}`,
        `p-${p || ''}`,
        `mb-${mb || ''}`,
        `mt-${mt || ''}`,
        `r-${r || ''}`,
        `flx-${flx || ''}`,
        `wrap-${wrap || ''}`,
        `gap-${gap || ''}`,
        `grow-${grow || ''}`,
        `ai-${ai || ''}`,
        `ac-${ac || ''}`,
        `jc-${jc || ''}`,
        `ov-${ov || ''}`,
        `background-${background || ''}`,
        `boundary-${boundary || ''}`,
        `height-${height || ''}`,
        `position-${position}`,
        `base-${base}`,
        isInactive && 'inactive',
        className,
      )}
      style={{
        minWidth: width,
        maxWidth: maxwidth,
        width: boxWidth,
        maxHeight: maxheight,
        minHeight: minheight,
        lineHeight,
        padding,
        gap: gapSize,
        borderRadius,
        boxShadow,
      }}
    >
      {children}
    </div>
  );
};

export default Box;
